import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue, pctColor } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import { Loader2, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import DmeTag from "@/components/DmeTag";

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Sparkline({ data, color }) {
  const points = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={points} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#g-${color})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function MoversList({ items, kind }) {
  const color = kind === "risers" ? "#10B981" : "#EF4444";
  const Icon = kind === "risers" ? TrendingUp : TrendingDown;
  return (
    <div className="card-bordered overflow-hidden">
      <div className="flex items-center gap-2 p-4 border-b border-[var(--border)]">
        <Icon className="w-4 h-4" style={{ color }} />
        <div className="font-display font-semibold">{kind === "risers" ? "Market Risers" : "Market Fallers"}</div>
      </div>
      <table className="w-full text-sm tabular">
        <thead className="text-[10px] tracking-widest text-[var(--text-3)]">
          <tr className="border-b border-[var(--border)]">
            <th className="text-left p-3">PLAYER</th>
            <th className="text-left p-3">POS</th>
            <th className="p-3">TREND</th>
            <th className="text-right p-3"><span className="inline-flex items-center gap-1.5 justify-end">VALUE <DmeTag /></span></th>
            <th className="text-right p-3">Δ</th>
          </tr>
        </thead>
        <tbody>
          {items.map(p => (
            <tr key={p.player_id} className="border-b border-[var(--border)] hover:bg-white/[0.03]" data-testid={`mover-${kind}-${p.player_id}`}>
              <td className="p-3 font-medium"><Link to={`/app/players/${p.player_id}`} className="hover:text-[var(--accent)]">{p.name}</Link></td>
              <td className="p-3 text-[var(--text-2)]">{p.position}</td>
              <td className="p-3 w-32"><Sparkline data={p.history} color={color} /></td>
              <td className="p-3 text-right">{formatValue(p.value)}</td>
              <td className={`p-3 text-right ${pctColor(p.change_pct)}`}>
                {p.change_pct > 0 ? "+" : ""}{p.change_pct.toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MarketTrends() {
  const [window, setWindow] = useState(7);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format } = useFormat();

  useEffect(() => {
    setLoading(true);
    api.get(`/market/trends?window=${window}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [window, format]);

  useEffect(() => {
    api.get(`/market/status`).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// MARKET TRENDS</div>
          <h1 className="font-display text-4xl font-bold" data-testid="mt-heading">Dynasty Value Movers</h1>
          <p className="text-[var(--text-2)] mt-2">Tracking shifts in Dynasty Metrics asset value across a rolling window.</p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-[var(--text-3)]" data-testid="mt-last-refresh">
            <RefreshCw className="w-3 h-3" />
            <span>
              Values last updated <span className="text-[var(--text-2)]">{timeAgo(status?.last_value_refresh)}</span>
            </span>
          </div>
        </div>
        <div className="inline-flex border border-[var(--border)] rounded-md overflow-hidden">
          {[1, 7, 14].map(w => (
            <button key={w} onClick={() => setWindow(w)} className={`px-4 py-2 text-sm ${window === w ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid={`mt-window-${w}`}>
              {w}d
            </button>
          ))}
        </div>
      </header>

      {loading || !data ? (
        <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MoversList items={data.risers} kind="risers" />
          <MoversList items={data.fallers} kind="fallers" />
        </div>
      )}
    </div>
  );
}
